import { useState } from "react";
import { ChevronLeft, CircleDot, Cable, Users, Boxes, Split, RadioTower, TriangleAlert, Settings, BarChart3, Radar } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const WIDGETS = {
  poles: { label: "Słupy", icon: CircleDot, color: "#EAB308", value: (d) => (d.poles || []).length },
  cables_length: {
    label: "Długość kabli", icon: Cable, color: "#3B82F6",
    value: (d) => {
      const m = (d.cables || []).reduce((s, c) => s + (Number(c.length_m) || 0), 0);
      return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m} m`;
    },
  },
  clients: { label: "Klienci", icon: Users, color: "#EF4444", value: (d) => (d.clients || []).length },
  closures: { label: "Mufy", icon: Boxes, color: "#A855F7", value: (d) => (d.closures || []).length },
  splitters: { label: "Splittery", icon: Split, color: "#F97316", value: (d) => (d.splitters || []).length },
  olt: { label: "OLT", icon: RadioTower, color: "#3B82F6", value: (d) => (d.olt || []).length },
  faults: {
    label: "Awarie otwarte", icon: TriangleAlert, color: "#EF4444",
    value: (d) => (d.faults || []).filter((f) => f.status !== "usunięta").length,
  },
  power_alerts: {
    label: "Alerty mocy", icon: TriangleAlert, color: "#F59E0B",
    value: (d, extra) => Object.values((extra && extra.powerStatus) || {}).filter((s) => s === "krytyczny" || s === "ostrzezenie").length,
  },
};

export const WIDGET_KEYS = Object.keys(WIDGETS);
export const WIDGET_LABELS = Object.fromEntries(WIDGET_KEYS.map((k) => [k, WIDGETS[k].label]));
const WIDGET_MODULE = {
  poles: "poles", cables_length: "cables", clients: "clients", closures: "closures",
  splitters: "splitters", olt: "olt", faults: "faults", power_alerts: "clients",
};

export default function StatsDrawer({ data, widgets, onOpenSettings, onOpenModule, extra }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const active = (widgets && widgets.length ? widgets : WIDGET_KEYS).filter((k) => WIDGETS[k]);
  const coverage = data.coverage || [];
  const groupBy = (field) => {
    const m = {};
    coverage.forEach((c) => { const k = c[field] || "—"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };

  return (
    <>
      {/* Hover trigger strip on right edge */}
      {!open && (
        <div
          className="absolute top-1/2 -translate-y-1/2 right-0 z-[744]"
          onMouseEnter={() => setOpen(true)}
          data-testid="stats-drawer-trigger"
        >
          <div className="bg-slate-900/90 backdrop-blur border border-r-0 border-slate-700 rounded-l-lg px-1.5 py-4 cursor-pointer hover:bg-slate-800 flex flex-col items-center gap-1 text-cyan-400">
            <ChevronLeft size={16} />
            <BarChart3 size={16} />
          </div>
        </div>
      )}

      {/* Sliding panel */}
      <div
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`absolute top-16 right-0 z-[745] w-64 transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="stats-drawer"
      >
        <div className="m-2 rounded-lg bg-slate-900/95 backdrop-blur-md border border-slate-700 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-800">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-300 flex items-center gap-2">
              <BarChart3 size={14} className="text-cyan-400" /> {t("Statystyki sieci")}
            </span>
            <button onClick={onOpenSettings} title={t("Konfiguruj kafelki")} data-testid="stats-drawer-settings"
              className="text-slate-400 hover:text-cyan-400"><Settings size={15} /></button>
          </div>
          <div className="p-2 grid grid-cols-1 gap-1.5">
            {active.map((k) => {
              const w = WIDGETS[k];
              const Icon = w.icon;
              return (
                <button key={k} data-testid={`stat-${k}`} type="button"
                  onClick={() => onOpenModule && WIDGET_MODULE[k] && onOpenModule(WIDGET_MODULE[k])}
                  className="w-full text-left flex items-center gap-3 rounded-md bg-slate-800/60 border border-slate-800 px-3 py-2.5 hover:border-cyan-600 hover:bg-slate-800 transition-colors cursor-pointer">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: `${w.color}22` }}>
                    <Icon size={16} style={{ color: w.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] text-slate-400 leading-tight">{t(w.label)}</div>
                    <div className="text-lg font-bold font-mono text-slate-100 leading-tight" data-testid={`stat-value-${k}`}>{w.value(data, extra)}</div>
                  </div>
                </button>
              );
            })}
            {active.length === 0 && (
              <div className="text-xs text-slate-500 px-2 py-3 text-center">{t("Brak wybranych kafelków. Skonfiguruj w Ustawieniach.")}</div>
            )}
          </div>
          {coverage.length > 0 && (
            <div className="border-t border-slate-800 p-2" data-testid="coverage-stats">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1.5">
                <Radar size={13} className="text-teal-400" /> {t("Pokrycie — zasięgi")}
                <span className="ml-auto font-mono text-teal-300" data-testid="coverage-total">{coverage.length}</span>
              </div>
              <div className="text-[10px] text-slate-500 mb-0.5">{t("wg medium")}</div>
              {groupBy("medium").map(([k, n]) => (
                <div key={k} className="flex justify-between text-[11px] px-1 py-0.5">
                  <span className="truncate text-slate-300 pr-2">{k}</span><span className="font-mono text-slate-400">{n}</span>
                </div>
              ))}
              <div className="text-[10px] text-slate-500 mt-2 mb-0.5">{t("wg technologii")}</div>
              {groupBy("technologia").map(([k, n]) => (
                <div key={k} className="flex justify-between text-[11px] px-1 py-0.5">
                  <span className="truncate text-slate-300 pr-2">{k}</span><span className="font-mono text-slate-400">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
